import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import {
    DropdownOptionButton,
    DropdownWrapper,
    IconChat6,
    IconContact1,
    IconInfoCircle,
    IconQuestionCircle,
    IconSignOut,
    leftNavConstants,
} from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import {
    constants,
    getAudioUploadsLocalStorageKey,
    getWebpackEnvVar,
    setInLocalStorage,
} from '@trqk/utils';

import {
    useTasks,
    useEntities,
    useModal,
    useStatementSelection,
} from '../../../hooks';
import { AboutTrqk } from '../../modals';
import { getFeatureFlags } from '../../../featureFlags';
import { PATHS } from '../../../constants';
import { initialStateData, INITIAL_STATE } from '../../../app/initialState';
import { AdminTools } from './components';

const { GET_SESSION, SIGN_OUT_WITH_COGNITO } = queryStrings;

const { HEIGHTS, WIDTHS } = leftNavConstants;
const width = WIDTHS.LEFT_NAV - 1; // allowing for 1px border

export const AccountDropdown = (): JSX.Element => {
    const { clientEventsPage } = getFeatureFlags();
    const clientId = getWebpackEnvVar('web', 'AWS_APP_CLIENT_ID');
    const userPoolId = getWebpackEnvVar('web', 'AWS_USER_POOL_ID');
    const [isExpanded, setIsExpanded] = useState(false);
    const history = useHistory();
    const [signOutWithCognito] = useMutation(SIGN_OUT_WITH_COGNITO, {
        onCompleted: () => {
            // TODO handle errors here
            setIsExpanded(false);
            history.push(PATHS.SIGN_IN);
        },
    });
    const sessionQuery = useQuery(GET_SESSION);
    const sessionIsValid = sessionQuery?.data?.session?.sessionIsValid ?? false;
    const userId = sessionQuery?.data?.session?.currentUser?.id ?? null;
    const { sendTaskEvent } = useTasks();
    const { sendEntitySelectionEvent } = useEntities();
    const {
        statementSelectionProps: { selectionId, sendStatementSelectionEvent },
    } = useStatementSelection();
    const modal = useModal();

    const list = [
        <DropdownOptionButton
            key="settings"
            handleClick={(): void => {
                history.push({
                    search: history.location.search,
                    pathname: PATHS.SETTINGS,
                });
            }}
            height={HEIGHTS.ACCOUNT_DROPDOWN}
            Icon={IconContact1}
            label="Settings"
        />,
        <DropdownOptionButton
            key="help"
            disabled={true}
            handleClick={(): void => 
                // TODO: show Help modal or page
                 undefined
            }
            height={HEIGHTS.ACCOUNT_DROPDOWN}
            Icon={IconQuestionCircle}
            label="Help"
        />,
        <DropdownOptionButton
            key="contact"
            handleClick={(): void => {
                setIsExpanded(false);
            }}
            height={HEIGHTS.ACCOUNT_DROPDOWN}
            Icon={IconChat6}
            label={
                <a href={`mailto: ${constants.INFO_EMAIL_ADDRESS}`}>Contact</a>
            }
        />,
        <DropdownOptionButton
            key="about"
            handleClick={(): void => {
                // setIsExpanded(false);

                modal.add({
                    Content: AboutTrqk,
                });
            }}
            height={HEIGHTS.ACCOUNT_DROPDOWN}
            Icon={IconInfoCircle}
            label="About Trqk"
        />,
        <DropdownOptionButton
            key="signOut"
            handleClick={(): void => {
                sendTaskEvent({
                    type: 'CANCEL_ALL_TASKS',
                    value: null,
                });
                sendEntitySelectionEvent({
                    type: 'CLEAR_CONTEXT',
                    value: {
                        userId,
                    },
                });
                sendStatementSelectionEvent({
                    type: 'CLEAR_CONTEXTS',
                    value: {
                        id: selectionId,
                        userId,
                    },
                });
                setInLocalStorage(
                    getAudioUploadsLocalStorageKey(userId),
                    (): null => null,
                );
                signOutWithCognito({
                    variables: {
                        clientId,
                        userPoolId,
                    },
                    context: {
                        initialStateData,
                        initialStateQuery: INITIAL_STATE,
                    },
                });
            }}
            height={HEIGHTS.ACCOUNT_DROPDOWN}
            Icon={IconSignOut}
            label="Sign Out"
        />,
    ];

    if (clientEventsPage) {
        list.unshift(
            <AdminTools
                key="adminTools"
                height={HEIGHTS.ACCOUNT_DROPDOWN}
                width={width}
            />,
        );
    }

    return (
        <DropdownWrapper
            boxShadowWhenOpen={false}
            disabled={!sessionIsValid}
            height={HEIGHTS.ACCOUNT_DROPDOWN}
            expansionDirection="up"
            isExpanded={isExpanded}
            selectedLabel={
                <div className="ml-3">
                    <IconContact1
                        fillColor="primary-500"
                        width={20}
                        addClasses="mr-2"
                    />
                    Account
                </div>
            }
            setIsExpanded={setIsExpanded}
            width={width}
            borderConfig="none"
        >
            {list}
        </DropdownWrapper>
    );
};
